import { ArrowRight, KeyRound, Mail } from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { Footer } from "../components/layout/Footer";
import { Navbar } from "../components/layout/Navbar";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";
import { trackApiKeyRequest, trackContactEmailClick, trackPromotionalFunnelEvent } from "../lib/analytics";

const SUPPORT_EMAIL = "support@gugudata.ai";
const GENERAL_INQUIRY_MAILTO = `mailto:${SUPPORT_EMAIL}`;
const API_KEY_REQUEST_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("GuGuData.AI API access request")}&body=${encodeURIComponent(
  "Please tell us about your AI use case, the data connectors you need, expected request volume, and target launch timeline."
)}`;

const Contact = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const intent = searchParams.get("intent") === "api-access" ? "api_access" : "general_contact";
  const sourceSurface = searchParams.get("source") || "direct";

  const trackContactCta = (ctaName: string, destination: string) => {
    trackPromotionalFunnelEvent("click_contact_sales", {
      surface: "contact_page",
      cta_name: ctaName,
      funnel_stage: "contact",
      destination,
      result_status: "clicked",
    });
  };

  useEffect(() => {
    trackPromotionalFunnelEvent("view_contact_page", {
      surface: "contact_page",
      funnel_stage: "contact",
      intent,
      source_surface: sourceSurface,
      result_status: "viewed",
    });
  }, [intent, sourceSurface]);

  // Initialize fade-in animations
  useEffect(() => {
    const fadeElements = document.querySelectorAll(".fade-in-section");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    fadeElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      fadeElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{t("meta.contact.title")}</title>
        <meta name="description" content={t("meta.contact.description")} />
        <meta name="keywords" content={t("meta.contact.keywords")} />
        <meta property="og:title" content={t("meta.contact.title")} />
        <meta property="og:description" content={t("meta.contact.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gugudata.ai/contact" />
        <meta property="og:image" content="https://www.gugudata.ai/og.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="GuGuData.AI" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta.contact.title")} />
        <meta name="twitter:description" content={t("meta.contact.description")} />
        <meta name="twitter:image" content="https://www.gugudata.ai/og.png" />
        <link rel="canonical" href="https://www.gugudata.ai/contact" />
        <html lang={language} />
      </Helmet>

      <Navbar />

      {/* Contact Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="fade-in-section">
            <div className="text-center mb-20">
              <h1 className="text-5xl md:text-6xl font-bold mb-4 text-gray-900 dark:text-white">{t("contact.title")}</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto font-light tracking-wide">{t("contact.subtitle")}</p>
            </div>

            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6 mb-20">
              <article className={`rounded-2xl border p-8 shadow-sm ${intent === "api_access" ? "border-brand-blue bg-blue-50 dark:bg-blue-950/30" : "border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"}`}>
                <div className="h-12 w-12 rounded-xl bg-brand-blue text-white flex items-center justify-center mb-5">
                  <KeyRound className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-semibold mb-3 text-gray-900 dark:text-white">{t("contact.apiAccess.title")}</h2>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{t("contact.apiAccess.description")}</p>
                <a
                  href={API_KEY_REQUEST_MAILTO}
                  onClick={() =>
                    trackApiKeyRequest({
                      surface: "contact_api_access",
                      ctaName: "request_api_key",
                      destination: GENERAL_INQUIRY_MAILTO,
                    })
                  }
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-brand-blue rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  {t("contact.apiAccess.cta")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t("contact.apiAccess.guidance")}</p>
              </article>

              <article className="rounded-2xl border border-gray-200 bg-gray-50 p-8 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="h-12 w-12 rounded-xl bg-gray-800 text-white flex items-center justify-center mb-5 dark:bg-gray-700">
                  <Mail className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-semibold mb-3 text-gray-900 dark:text-white">{t("contact.general.title")}</h2>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{t("contact.general.description")}</p>
                <a
                  href={GENERAL_INQUIRY_MAILTO}
                  onClick={() =>
                    trackContactEmailClick({
                      surface: "contact_general_inquiry",
                      ctaName: "email_team",
                      destination: GENERAL_INQUIRY_MAILTO,
                    })
                  }
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  {t("contact.general.cta")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </article>
            </div>

            {/* Company Introduction */}
            <div className="max-w-4xl mx-auto mb-24">
              <div className="prose dark:prose-invert max-w-none">
                {/* Introduction Text */}
                <div className="mb-20 max-w-3xl mx-auto">
                  <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 font-light tracking-wide text-center">{t("contact.intro.paragraph1")}</p>
                </div>

                {/* Feature Cards */}
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="group">
                    <div className="p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
                      <h3 className="text-2xl font-medium mb-6 text-gray-900 dark:text-white">{t("contact.sections.coreAdvantages")}</h3>
                      <p className="text-gray-600 dark:text-gray-400 font-light leading-7 flex-grow" style={{ fontSize: "15px" }}>
                        {t("contact.intro.paragraph2")}
                      </p>
                    </div>
                  </div>

                  <div className="group">
                    <div className="p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
                      <h3 className="text-2xl font-medium mb-6 text-gray-900 dark:text-white">{t("contact.sections.technicalFeatures")}</h3>
                      <p className="text-gray-600 dark:text-gray-400 font-light leading-7 flex-grow" style={{ fontSize: "15px" }}>
                        {t("contact.intro.paragraph3")}
                      </p>
                    </div>
                  </div>

                  <div className="group">
                    <div className="p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
                      <h3 className="text-2xl font-medium mb-6 text-gray-900 dark:text-white">{t("contact.sections.solutions")}</h3>
                      <p className="text-gray-600 dark:text-gray-400 font-light leading-7 flex-grow" style={{ fontSize: "15px" }}>
                        {t("contact.intro.paragraph4")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bottom Text */}
                <div className="mt-20 max-w-3xl mx-auto">
                  <div className="text-center space-y-6">
                    <p className="text-gray-600 dark:text-gray-400 font-light tracking-wide leading-relaxed">{t("contact.intro.paragraph5")}</p>
                    <p className="text-gray-600 dark:text-gray-400 font-light tracking-wide leading-relaxed">{t("contact.intro.paragraph6")}</p>
                    <div className="flex justify-center gap-4 mt-8">
                      <a
                        href="/"
                        onClick={() => trackContactCta("back_to_home", "/")}
                        className="inline-flex items-center px-6 py-3 text-sm font-medium text-brand-blue bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        Back to Home
                      </a>
                      <a
                        href="/document"
                        onClick={() => trackContactCta("view_documentation", "/document")}
                        className="inline-flex items-center px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        View Documentation
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="max-w-2xl mx-auto">
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-12 text-gray-900 dark:text-white">{t("contact.info.title")}</h2>
                <div className="flex flex-col items-center space-y-8">
                  <div className="inline-flex items-center space-x-2">
                    <span className="w-2 h-2 bg-brand-blue rounded-full"></span>
                    <span className="text-gray-900 dark:text-white font-medium">{t("contact.info.company")}</span>
                  </div>

                  <div className="flex flex-col items-center text-gray-600 dark:text-gray-400 font-light space-y-1">
                    <span>{t("contact.info.address")}</span>
                  </div>

                  <div className="w-12 h-px bg-gray-200 dark:bg-gray-800"></div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <div className="flex flex-col items-center">
                      <a
                        href={GENERAL_INQUIRY_MAILTO}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          trackContactEmailClick({
                            surface: "contact_information",
                            ctaName: "email_support",
                            destination: GENERAL_INQUIRY_MAILTO,
                          })
                        }
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 w-[280px] justify-center whitespace-nowrap"
                      >
                        <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {t("contact.info.email")}
                      </a>
                      <span className="mt-2 text-sm text-gray-500 dark:text-gray-400">{SUPPORT_EMAIL}</span>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
